import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Image, 
  Video, 
  Loader2,
  Eye,
  Upload,
  Sofa,
  Mail,
  ImagePlus,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = {
  portfolio: ['sala', 'dormitorio', 'cocina', 'oficina', 'baño', 'exterior'],
  tips: ['decoracion', 'colores', 'muebles', 'organizacion', 'tendencias', 'diy']
};

export default function AdminPanel() {
  const [token, setToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('portfolio');
  const [isLoading, setIsLoading] = useState(false);
  
  // Portfolio state
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [portfolioForm, setPortfolioForm] = useState({
    title: '',
    description: '',
    category: 'sala',
    image_before: '',
    image_after: '',
    gallery: []
  });
  
  // Tips state
  const [tips, setTips] = useState([]);
  const [tipForm, setTipForm] = useState({
    title: '',
    content: '',
    image_url: '',
    video_url: '',
    category: 'decoracion'
  });

  // Requests state (furniture orders + contacts)
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        setToken(data.token);
        setIsAuthenticated(true);
        setPassword('');
        toast.success('¡Bienvenida Pamela!');
      } else {
        toast.error(data.detail || 'Contraseña incorrecta');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setIsAuthenticated(false);
  };

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  });

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/portfolio`);
      const data = await res.json();
      setPortfolioItems(data.items || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching portfolio:', error);
    }
  }, []);

  const fetchTips = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tips`);
      const data = await res.json();
      setTips(data.tips || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching tips:', error);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/furniture-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching orders:', error);
    }
  }, [token]);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setContacts(data.contacts || []);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching contacts:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPortfolio();
      fetchTips();
      fetchOrders();
      fetchContacts();
    }
  }, [isAuthenticated, fetchPortfolio, fetchTips, fetchOrders, fetchContacts]);

  const handlePortfolioImageUpload = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor sube un archivo de imagen.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1400;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim; }
          else { width = Math.round(width * maxDim / height); height = maxDim; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', 0.75);
        setPortfolioForm((prev) => ({ ...prev, [field]: compressed }));
        toast.success('Foto agregada');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const maxDim = 1400;
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim; }
            else { width = Math.round(width * maxDim / height); height = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const compressed = canvas.toDataURL('image/jpeg', 0.75);
          setPortfolioForm((prev) => ({ ...prev, gallery: [...prev.gallery, compressed] }));
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handlePortfolioSubmit = async (e) => {
    e.preventDefault();
    if (!portfolioForm.image_after) {
      toast.error('La imagen del resultado es obligatoria');
      return;
    }
    
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/portfolio`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(portfolioForm)
      });
      const data = await res.json();
      
      if (data.ok) {
        toast.success('¡Proyecto agregado!');
        setPortfolioForm({ title: '', description: '', category: 'sala', image_before: '', image_after: '', gallery: [] });
        fetchPortfolio();
      } else {
        toast.error(data.detail || 'Error al agregar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTipSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tips`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(tipForm)
      });
      const data = await res.json();
      
      if (data.ok) {
        toast.success('¡Tip agregado!');
        setTipForm({ title: '', content: '', image_url: '', video_url: '', category: 'decoracion' });
        fetchTips();
      } else {
        toast.error(data.detail || 'Error al agregar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  const deletePortfolioItem = async (id) => {
    if (!window.confirm('¿Eliminar este proyecto?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/portfolio/${id}`, { method: 'DELETE', headers: authHeaders() });
      toast.success('Proyecto eliminado');
      fetchPortfolio();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const deleteTip = async (id) => {
    if (!window.confirm('¿Eliminar este tip?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/tips/${id}`, { method: 'DELETE', headers: authHeaders() });
      toast.success('Tip eliminado');
      fetchTips();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-beige-100 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-2xl shadow-card max-w-md w-full"
        >
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_brave-cohen-2/artifacts/y5uu8k52_Decorando%20Vamos%20LOGO%20NEW.png"
              alt="Logo"
              className="w-24 h-24 mx-auto mb-4"
            />
            <h1 className="font-heading text-2xl">Panel de Administración</h1>
            <p className="text-muted-foreground">Ingresa tu contraseña</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              className="w-full px-4 py-3 rounded-lg border border-beige-300 focus:border-olive-500"
              data-testid="admin-password-input"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-olive-500 hover:bg-olive-600 disabled:bg-olive-300 text-white py-3 rounded-full font-medium flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {isLoading && <Loader2 className="animate-spin" size={18} />}
              Ingresar
            </button>
          </form>
          <a 
            href="/"
            className="block text-center mt-6 text-olive-500 hover:underline"
          >
            ← Volver a la página principal
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-beige-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-olive-500 hover:text-olive-600">
              <ArrowLeft size={24} />
            </a>
            <h1 className="font-heading text-xl">Panel de Administración</h1>
          </div>
          <div className="flex items-center gap-6">
            <a 
              href="/" 
              target="_blank"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-olive-500"
            >
              <Eye size={18} /> Ver página
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-red-500"
              data-testid="admin-logout-btn"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('portfolio')}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'portfolio' 
                ? 'bg-olive-500 text-white' 
                : 'bg-white text-foreground hover:bg-beige-200'
            }`}
            data-testid="tab-portfolio"
          >
            <Image className="inline mr-2" size={18} />
            Proyectos ({portfolioItems.length})
          </button>
          <button
            onClick={() => setActiveTab('tips')}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'tips' 
                ? 'bg-olive-500 text-white' 
                : 'bg-white text-foreground hover:bg-beige-200'
            }`}
            data-testid="tab-tips"
          >
            <Video className="inline mr-2" size={18} />
            Tips ({tips.length})
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'orders'
                ? 'bg-olive-500 text-white'
                : 'bg-white text-foreground hover:bg-beige-200'
            }`}
            data-testid="tab-orders"
          >
            <Sofa className="inline mr-2" size={18} />
            Pedidos de Muebles ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'contacts'
                ? 'bg-olive-500 text-white'
                : 'bg-white text-foreground hover:bg-beige-200'
            }`}
            data-testid="tab-contacts"
          >
            <Mail className="inline mr-2" size={18} />
            Contactos ({contacts.length})
          </button>
        </div>

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Form */}
            <div className="bg-white p-6 rounded-2xl shadow-card">
              <h2 className="font-heading text-xl mb-4">Agregar Proyecto</h2>
              <form onSubmit={handlePortfolioSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Título *</label>
                  <input
                    type="text"
                    required
                    value={portfolioForm.title}
                    onChange={(e) => setPortfolioForm({...portfolioForm, title: e.target.value})}
                    placeholder="Ej: Renovación Sala Moderna"
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500"
                    data-testid="portfolio-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Categoría *</label>
                  <select
                    value={portfolioForm.category}
                    onChange={(e) => setPortfolioForm({...portfolioForm, category: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500"
                    data-testid="portfolio-category-select"
                  >
                    {categories.portfolio.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Descripción</label>
                  <textarea
                    value={portfolioForm.description}
                    onChange={(e) => setPortfolioForm({...portfolioForm, description: e.target.value})}
                    placeholder="Describe el proyecto..."
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500 h-20 resize-none"
                    data-testid="portfolio-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Foto principal del proyecto *</label>
                  {!portfolioForm.image_after ? (
                    <label htmlFor="portfolio-after-upload" className="flex flex-col items-center justify-center gap-2 w-full py-8 rounded-lg border-2 border-dashed border-beige-300 hover:border-olive-500 cursor-pointer transition-colors text-muted-foreground hover:text-olive-500" data-testid="portfolio-after-dropzone">
                      <ImagePlus size={28} />
                      <span className="text-sm font-medium">Toca para subir la foto</span>
                      <span className="text-xs">JPG o PNG</span>
                      <input id="portfolio-after-upload" type="file" accept="image/*" onChange={(e) => handlePortfolioImageUpload(e, 'image_after')} className="hidden" data-testid="portfolio-after-input" />
                    </label>
                  ) : (
                    <div className="relative w-full rounded-lg overflow-hidden border border-beige-300" data-testid="portfolio-after-preview">
                      <img src={portfolioForm.image_after} alt="Foto principal" className="w-full max-h-48 object-cover" />
                      <button type="button" onClick={() => setPortfolioForm({...portfolioForm, image_after: ''})} className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md" aria-label="Quitar foto">
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Foto "Antes" (opcional)</label>
                  {!portfolioForm.image_before ? (
                    <label htmlFor="portfolio-before-upload" className="flex flex-col items-center justify-center gap-2 w-full py-6 rounded-lg border-2 border-dashed border-beige-300 hover:border-olive-500 cursor-pointer transition-colors text-muted-foreground hover:text-olive-500" data-testid="portfolio-before-dropzone">
                      <ImagePlus size={24} />
                      <span className="text-sm font-medium">Subir foto del antes</span>
                      <input id="portfolio-before-upload" type="file" accept="image/*" onChange={(e) => handlePortfolioImageUpload(e, 'image_before')} className="hidden" data-testid="portfolio-before-input" />
                    </label>
                  ) : (
                    <div className="relative w-full rounded-lg overflow-hidden border border-beige-300" data-testid="portfolio-before-preview">
                      <img src={portfolioForm.image_before} alt="Foto antes" className="w-full max-h-40 object-cover" />
                      <button type="button" onClick={() => setPortfolioForm({...portfolioForm, image_before: ''})} className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md" aria-label="Quitar foto">
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Más fotos del proyecto (opcional)</label>
                  <label htmlFor="portfolio-gallery-upload" className="flex items-center justify-center gap-2 w-full py-4 rounded-lg border-2 border-dashed border-beige-300 hover:border-olive-500 cursor-pointer transition-colors text-muted-foreground hover:text-olive-500" data-testid="portfolio-gallery-dropzone">
                    <ImagePlus size={20} />
                    <span className="text-sm font-medium">Agregar varias fotos</span>
                    <input id="portfolio-gallery-upload" type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" data-testid="portfolio-gallery-input" />
                  </label>
                  {portfolioForm.gallery.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3" data-testid="portfolio-gallery-preview">
                      {portfolioForm.gallery.map((g, i) => (
                        <div key={i} className="relative rounded-lg overflow-hidden border border-beige-300">
                          <img src={g} alt={`Foto ${i + 1}`} className="w-full h-20 object-cover" />
                          <button type="button" onClick={() => setPortfolioForm({...portfolioForm, gallery: portfolioForm.gallery.filter((_, idx) => idx !== i)})} className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow" aria-label="Quitar">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-olive-500 hover:bg-olive-600 disabled:bg-olive-300 text-white py-3 rounded-full font-medium flex items-center justify-center gap-2"
                  data-testid="portfolio-submit-btn"
                >
                  {isLoading ? <Loader2 className="animate-spin" size={20} /> : <Plus size={20} />}
                  Agregar Proyecto
                </button>
              </form>
            </div>

            {/* List */}
            <div>
              <h2 className="font-heading text-xl mb-4">Proyectos Guardados</h2>
              {portfolioItems.length === 0 ? (
                <div className="bg-white p-8 rounded-2xl text-center text-muted-foreground">
                  <Image size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Aún no hay proyectos</p>
                  <p className="text-sm">Agrega tu primer proyecto antes/después</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {portfolioItems.map(item => (
                    <div key={item.id} className="bg-white p-4 rounded-xl flex gap-4">
                      <img 
                        src={item.image_after} 
                        alt={item.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium">{item.title}</h3>
                        <p className="text-sm text-muted-foreground capitalize">{item.category}</p>
                        <p className="text-xs text-muted-foreground mt-1">{item.description?.slice(0, 60)}...</p>
                      </div>
                      <button
                        onClick={() => deletePortfolioItem(item.id)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-lg h-fit"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Form */}
            <div className="bg-white p-6 rounded-2xl shadow-card">
              <h2 className="font-heading text-xl mb-4">Agregar Tip</h2>
              <form onSubmit={handleTipSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Título *</label>
                  <input
                    type="text"
                    required
                    value={tipForm.title}
                    onChange={(e) => setTipForm({...tipForm, title: e.target.value})}
                    placeholder="Ej: 5 formas de iluminar tu sala"
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500"
                    data-testid="tip-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Categoría *</label>
                  <select
                    value={tipForm.category}
                    onChange={(e) => setTipForm({...tipForm, category: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500"
                    data-testid="tip-category-select"
                  >
                    {categories.tips.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contenido *</label>
                  <textarea
                    required
                    value={tipForm.content}
                    onChange={(e) => setTipForm({...tipForm, content: e.target.value})}
                    placeholder="Escribe tu tip o consejo..."
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500 h-32 resize-none"
                    data-testid="tip-content-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">URL Imagen (opcional)</label>
                  <input
                    type="url"
                    value={tipForm.image_url}
                    onChange={(e) => setTipForm({...tipForm, image_url: e.target.value})}
                    placeholder="https://..."
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500"
                    data-testid="tip-image-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">URL Video YouTube/Instagram (opcional)</label>
                  <input
                    type="url"
                    value={tipForm.video_url}
                    onChange={(e) => setTipForm({...tipForm, video_url: e.target.value})}
                    placeholder="https://youtube.com/... o https://instagram.com/..."
                    className="w-full px-4 py-2 rounded-lg border border-beige-300 focus:border-olive-500"
                    data-testid="tip-video-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-olive-500 hover:bg-olive-600 disabled:bg-olive-300 text-white py-3 rounded-full font-medium flex items-center justify-center gap-2"
                  data-testid="tip-submit-btn"
                >
                  {isLoading ? <Loader2 className="animate-spin" size={20} /> : <Plus size={20} />}
                  Agregar Tip
                </button>
              </form>
            </div>

            {/* List */}
            <div>
              <h2 className="font-heading text-xl mb-4">Tips Guardados</h2>
              {tips.length === 0 ? (
                <div className="bg-white p-8 rounded-2xl text-center text-muted-foreground">
                  <Video size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Aún no hay tips</p>
                  <p className="text-sm">Agrega tu primer consejo de decoración</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {tips.map(tip => (
                    <div key={tip.id} className="bg-white p-4 rounded-xl">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium">{tip.title}</h3>
                          <p className="text-sm text-muted-foreground capitalize">{tip.category}</p>
                        </div>
                        <button
                          onClick={() => deleteTip(tip.id)}
                          className="text-red-500 hover:bg-red-50 p-2 rounded-lg"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                      <p className="text-sm mt-2 text-muted-foreground">{tip.content.slice(0, 100)}...</p>
                      <div className="flex gap-2 mt-2">
                        {tip.image_url && <span className="text-xs bg-beige-200 px-2 py-1 rounded">🖼 Imagen</span>}
                        {tip.video_url && <span className="text-xs bg-beige-200 px-2 py-1 rounded">🎬 Video</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Furniture Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <h2 className="font-heading text-xl mb-4">Pedidos de Muebles</h2>
            {orders.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl text-center text-muted-foreground" data-testid="orders-empty">
                <Sofa size={48} className="mx-auto mb-4 opacity-50" />
                <p>Aún no hay pedidos de muebles</p>
                <p className="text-sm">Las solicitudes del formulario aparecerán aquí</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="orders-list">
                {orders.map((order, idx) => (
                  <div key={order.id || idx} className="bg-white p-5 rounded-xl shadow-card">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-lg">{order.name}</h3>
                      <span className="text-xs text-muted-foreground">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('es-PA') : ''}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p><span className="font-medium text-foreground">Categoría:</span> <span className="capitalize">{order.furniture_type}</span></p>
                      {order.style && <p><span className="font-medium text-foreground">Estilo:</span> <span className="capitalize">{order.style}</span></p>}
                      <p><span className="font-medium text-foreground">Presupuesto:</span> ${order.budget_min} - ${order.budget_max}</p>
                      {order.additional_notes && <p><span className="font-medium text-foreground">Notas:</span> {order.additional_notes}</p>}
                      <p><span className="font-medium text-foreground">Email:</span> {order.email}</p>
                      <p><span className="font-medium text-foreground">Teléfono:</span> {order.phone}</p>
                    </div>
                    {order.space_image && (
                      <a href={order.space_image} target="_blank" rel="noopener noreferrer" className="block mt-3" data-testid="order-space-image">
                        <img src={order.space_image} alt="Espacio del cliente" className="w-full max-h-48 object-cover rounded-lg border border-beige-200 hover:opacity-90 transition-opacity" />
                        <span className="text-xs text-muted-foreground">📷 Foto del espacio (clic para ampliar)</span>
                      </a>
                    )}
                    <a
                      href={`https://wa.me/${(order.phone || '').replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-sm text-[#25D366] hover:underline font-medium"
                    >
                      Responder por WhatsApp →
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <h2 className="font-heading text-xl mb-4">Contactos Recibidos</h2>
            {contacts.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl text-center text-muted-foreground" data-testid="contacts-empty">
                <Mail size={48} className="mx-auto mb-4 opacity-50" />
                <p>Aún no hay contactos</p>
                <p className="text-sm">Los mensajes del formulario de contacto aparecerán aquí</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="contacts-list">
                {contacts.map((contact, idx) => (
                  <div key={contact.id || idx} className="bg-white p-5 rounded-xl shadow-card">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-lg">{contact.name}</h3>
                      <span className="text-xs text-muted-foreground">
                        {contact.created_at ? new Date(contact.created_at).toLocaleDateString('es-PA') : ''}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {contact.service_interest && <p><span className="font-medium text-foreground">Interés:</span> {contact.service_interest}</p>}
                      {contact.message && <p><span className="font-medium text-foreground">Mensaje:</span> {contact.message}</p>}
                      <p><span className="font-medium text-foreground">Email:</span> {contact.email}</p>
                      {contact.phone && <p><span className="font-medium text-foreground">Teléfono:</span> {contact.phone}</p>}
                    </div>
                    {contact.phone && (
                      <a
                        href={`https://wa.me/${(contact.phone || '').replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-3 text-sm text-[#25D366] hover:underline font-medium"
                      >
                        Responder por WhatsApp →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
